import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API, apiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ScrollText, ShieldCheck, Download } from "lucide-react";

const SOURCE_LABELS = {
  info_lead: "Landing informativa",
  public_request: "Richiesta partner",
  public_intake: "Modulo dati",
  catnat_public: "CAT NAT pubblico",
  protezione_famiglia: "Protezione Famiglia",
};

export default function Audit() {
  const [items, setItems] = useState([]);
  const [consents, setConsents] = useState([]);
  const [consentTotal, setConsentTotal] = useState(0);

  useEffect(() => {
    api.get("/admin/audit").then(({ data }) => setItems(data));
    api.get("/admin/consents").then(({ data }) => { setConsents(data.items || []); setConsentTotal(data.total || 0); }).catch(() => {});
  }, []);

  const exportConsents = async () => {
    try {
      const { data } = await api.get("/admin/consents/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "registro_consensi.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl font-semibold text-forest">Audit trail</h1>
        <p className="mt-1 text-muted-foreground">Traccia immutabile di stati, documenti e deroghe (retention ≥ 5 anni).</p>
      </div>

      {/* Registro consensi privacy (GDPR) */}
      <Card className="overflow-hidden rounded-xl border-border/60" data-testid="consents-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 bg-[#EEF1FA] px-4 py-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-forest" />
            <div>
              <h2 className="font-heading text-lg font-semibold text-forest">Registro consensi privacy (GDPR)</h2>
              <p className="text-xs text-muted-foreground">Prova documentale della raccolta del consenso: {consentTotal} consensi registrati.</p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={exportConsents} data-testid="consents-export" className="gap-1.5 text-forest">
            <Download className="h-4 w-4" /> Esporta CSV
          </Button>
        </div>
        <Table>
          <TableHeader><TableRow className="bg-sage/50">
            <TableHead>Data/ora</TableHead><TableHead>Fonte</TableHead><TableHead>Nominativo</TableHead>
            <TableHead>Email</TableHead><TableHead>Prodotto</TableHead><TableHead>IP</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {consents.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">Nessun consenso registrato.</TableCell></TableRow>}
            {consents.map((c) => (
              <TableRow key={c.id} data-testid={`consent-${c.id}`}>
                <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{c.created_at ? new Date(c.created_at).toLocaleString("it-IT") : "—"}</TableCell>
                <TableCell><Badge variant="outline" className="border-forest/20 text-forest">{SOURCE_LABELS[c.source] || c.source}</Badge></TableCell>
                <TableCell className="text-sm font-medium text-forest">{c.subject_name || "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{c.subject_email || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{c.product_code || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{c.ip || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="overflow-hidden rounded-xl border-border/60">
        <div className="border-b border-border/60 px-4 py-3">
          <h2 className="font-heading text-lg font-semibold text-forest">Eventi di sistema</h2>
        </div>
        <Table>
          <TableHeader><TableRow className="bg-sage/50">
            <TableHead>Quando</TableHead><TableHead>Entità</TableHead><TableHead>Azione</TableHead><TableHead>Attore</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={4} className="py-10 text-center text-muted-foreground"><ScrollText className="mx-auto mb-2 h-6 w-6 opacity-40" /> Nessun evento.</TableCell></TableRow>}
            {items.map((e) => (
              <TableRow key={e.id} data-testid={`audit-${e.id}`}>
                <TableCell className="text-xs text-muted-foreground">{new Date(e.at).toLocaleString("it-IT")}</TableCell>
                <TableCell><Badge variant="outline" className="border-forest/20 text-forest">{e.entity}</Badge></TableCell>
                <TableCell className="text-sm font-medium text-forest">{e.action}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{e.actor}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
